import { EventStore, mapEventsToStore } from "applesauce-core";
import { persistEventsToCache } from "applesauce-core/helpers";
import type { Filter } from "applesauce-core/helpers";
import {
  createAddressLoader,
  createEventLoaderForStore,
  createReactionsLoader,
  createZapsLoader,
  DnsIdentityLoader,
} from "applesauce-loaders/loaders";
import { RelayLiveness, RelayPool, onlyEvents } from "applesauce-relay";
import type { Relay, RelayGroup } from "applesauce-relay";
import { NostrConnectSigner } from "applesauce-signers";
import type { NostrEvent } from "nostr-tools";
import { verifyEvent } from "nostr-tools";
import {
  Observable,
  Subscription,
  merge,
  distinctUntilChanged,
  firstValueFrom,
  of,
  timer,
} from "rxjs";
import { filter, map, take, timeout } from "rxjs/operators";
import { MailboxesModel } from "applesauce-core/models";
import { cacheRequest, purgeEventsFromCache, saveEvents } from "./cache";
import { nip05IdbCache, loadAllNip05FromIdb } from "./nip05IdbCache";
import { setHintEventStore } from "@/factories/hints";
import {
  fallbackRelays,
  lookupRelays,
  gitIndexRelays,
  relayCurationMode,
} from "./settings";
import {
  ISSUE_KIND,
  PATCH_KIND,
  PR_ROOT_KINDS,
  LEGACY_REPLY_KINDS,
  COVER_NOTE_KIND,
  REPO_KIND,
  parseRepoCoordinate,
  resolveChain,
} from "@/lib/nip34";
import { CI_EVENT_KINDS, CI_RUN_KIND } from "@/lib/ci";
import {
  createPaginatedTagValueLoader,
  type PaginatedTagValueResponse,
} from "@/lib/tagValuePaginatedLoader";
import { resilientSubscription } from "@/lib/resilientSubscription";
import {
  outboxStore,
  type RelayGroupFilter,
  type RelayGroupResolver,
} from "./outbox";
import { normalizeUrl } from "@/lib/url";
import { relayGroupUrls$ } from "@/models/RepositoryRelayGroup";
import {
  beginPrivateRepositorySession,
  classifyPrivateRepositoryEvent,
  getPrivateRelayEventIds,
  isActivePrivateGitRelay,
  isQuarantinedPrivateEventId,
  markPrivateRelayEvent,
  privateGitRelayUrls$,
  reconcileVerifiedPrivateGitCloneUrls,
  replacePrivateRepositoryCoordinates,
} from "./privateRepoAccess";

/**
 * Global EventStore instance for all Nostr events.
 * This is the central state container for the application.
 */
export const eventStore = new EventStore({
  keepDeleted: false, // Don't keep deleted events
  keepExpired: false, // Don't keep expired events
  keepOldVersions: false, // Only keep latest version of replaceable events
});

// Verify events when they are added to the store
eventStore.verifyEvent = verifyEvent;

/**
 * Keep the private-coordinate classifier current before the cache persistence
 * subscriber observes an insertion. This makes quarantine synchronous for
 * optimistic local events as well as relay events.
 */
function refreshPrivateRepositoryQuarantine(): void {
  const announcements = eventStore.getByFilters({ kinds: [REPO_KIND] });
  const privateCoordinates = new Set<string>();
  for (const announcement of announcements) {
    const identifier = announcement.tags.find(([name]) => name === "d")?.[1];
    if (!identifier) continue;
    const resolved = resolveChain(
      announcements,
      announcement.pubkey,
      identifier,
    );
    if (!resolved?.isPrivate) continue;
    for (const coordinate of resolved.allCoordinates) {
      privateCoordinates.add(coordinate);
    }
    for (const resolvedAnnouncement of resolved.announcements) {
      markPrivateRelayEvent(resolvedAnnouncement);
    }
  }
  replacePrivateRepositoryCoordinates(privateCoordinates);
  reconcileVerifiedPrivateGitCloneUrls(announcements);

  propagatePrivateRepositoryQuarantine();
}

function propagatePrivateRepositoryQuarantine(): void {
  // Reclassify the complete in-memory graph to handle a private tag or
  // maintainer announcement arriving after its ancestors and related events.
  // Repeat until references to newly quarantined events reach a fixed point.
  const events = eventStore.getByFilters({ since: 0 } as Filter);
  let changed: boolean;
  do {
    changed = false;
    for (const candidate of events) {
      const wasPrivate = isQuarantinedPrivateEventId(candidate.id);
      if (classifyPrivateRepositoryEvent(candidate) && !wasPrivate) {
        changed = true;
      }
    }
  } while (changed);

  const privateIds = new Set(getPrivateRelayEventIds());
  const quarantined = events.filter((candidate) =>
    privateIds.has(candidate.id),
  );
  if (quarantined.length > 0) {
    void purgeEventsFromCache(quarantined);
  }
}

eventStore.insert$.subscribe((event) => {
  if (event.kind === REPO_KIND) refreshPrivateRepositoryQuarantine();
  else {
    const wasPrivate = isQuarantinedPrivateEventId(event.id);
    if (classifyPrivateRepositoryEvent(event) || wasPrivate) {
      propagatePrivateRepositoryQuarantine();
    }
  }
});

// Persist events to the local nostrdb
persistEventsToCache(eventStore, saveEvents);

// Register this store as the source for factory relay-hint resolution. Done
// here (rather than `hints.ts` importing this module) so the factory layer has
// no static dependency on the service graph — see src/factories/hints.ts.
setHintEventStore(eventStore);

/**
 * Global RelayPool instance for all relay connections.
 * Use this to query events and publish to relays.
 */
export const pool = new RelayPool();

/**
 * Global relay liveness tracker.
 *
 * Tracks online / offline / dead state for every relay the pool sees. Used
 * for status indicators, persistence, and (in future) filtering relay
 * suggestion lists. **Not** consulted for reconnect cadence — that is owned
 * solely by the reconnectTimer override below, so this layer is purely
 * informational and does not influence retry behavior in resilientSubscription.
 */
export const liveness = new RelayLiveness();
liveness.connectToPool(pool);

// Replace each relay's reconnectTimer with a 3-phase backoff curve. This is
// the **single source of truth** for socket-level reconnect cadence — no
// other layer (resilientSubscription, RelayLiveness, etc.) governs WS retry
// timing.
//
// applesauce-relay@6.0.0 uses 1.5^attempts × 1000ms capped at 5min, which
// produces a tight burst (1.5s, 2.25s, 3.4s, 5s, 7.6s, 11s, …) that hammers
// 404 / unreachable URLs with many connection attempts before slowing down.
//
// Three-phase replacement:
//   - attempts 1-3:  1s,  2s,  4s          (network blip / relay restart)
//   - attempts 4-6:  30s, 60s, 2min        (server likely under load)
//   - attempts 7-9:  5min, 10min, 20min    (probably down)
//   - attempts 10+:  30min cap             (low-cost periodic probe)
//
// No termination — applesauce's share+keepAlive on watchTower naturally
// pauses the reconnect loop when no callers are subscribed (after a 30s
// keep-alive window with refCount=0 the WS closes cleanly and no further
// startReconnectTimer fires). attempts$ persists on the Relay instance, so
// when a caller comes back later the curve resumes from where it left off.
// While a caller is subscribed, capping at 30min gives auto-revival probes
// for relays that have come back without manual intervention.
//
// Requires the patches/applesauce-relay@6.0.0.patch which makes
// RelayPool.relay() emit on pool.add$ — without that, this subscribe never
// fires for newly-created relays.
const RECONNECT_PHASES_MS: number[] = [
  // Phase 1 — burst (attempts 1-3)
  1_000,
  2_000,
  4_000,
  // Phase 2 — under-load pause (attempts 4-6)
  30_000,
  60_000,
  120_000,
  // Phase 3 — probably down (attempts 7-9)
  5 * 60_000,
  10 * 60_000,
  20 * 60_000,
];
const RECONNECT_CAP_MS = 30 * 60_000;

function reconnectDelayMs(attempts: number): number {
  // attempts is 1-based for the first failure; use index attempts-1.
  const i = Math.max(0, attempts - 1);
  return i < RECONNECT_PHASES_MS.length
    ? RECONNECT_PHASES_MS[i]
    : RECONNECT_CAP_MS;
}

pool.add$.subscribe((relay) => {
  relay.reconnectTimer = (_error, attempts) =>
    timer(reconnectDelayMs(attempts));

  // Record provenance before event-store subscribers see the event. Events
  // received through a private relay are session-only and never enter the
  // shared IndexedDB cache.
  relay.message$.subscribe((message: unknown) => {
    if (
      !isActivePrivateGitRelay(relay.url) ||
      !Array.isArray(message) ||
      message[0] !== "EVENT"
    ) {
      return;
    }
    const event = message[2];
    if (
      event &&
      typeof event === "object" &&
      "id" in event &&
      typeof event.id === "string"
    ) {
      markPrivateRelayEvent(event as NostrEvent);
    }
  });
});

/**
 * Setup NostrConnectSigner to use the global relay pool.
 * This allows NostrConnectSigner instances to communicate with relays
 * for NIP-46 remote signing.
 */
NostrConnectSigner.pool = pool;

// Inject the pool into the outbox store so it can publish to relays.
// This is done here (after pool is created) to avoid a circular dependency.
outboxStore.pool = pool;

/**
 * NIP-42 auto-auth policy — wired once at the pool level.
 *
 * When a relay sends an AUTH challenge we check (synchronously from the
 * EventStore cache) whether the relay URL appears in the active account's
 * NIP-65 inbox/outbox list or decrypted kind 10318 private Git relay list.
 * If it does, we authenticate immediately.
 * Unknown relays are silently skipped — authenticating to arbitrary relays
 * would leak the user's identity to any relay that asks.
 *
 * The active account is read lazily at challenge time so this works correctly
 * after account switches without needing to re-wire.  accounts.ts imports
 * nostr.ts (not the other way around) so we import accounts lazily here to
 * avoid a circular dependency.
 */
async function authenticateTrustedRelay(relay: Relay): Promise<void> {
  // Lazy import to avoid circular dependency (accounts → nostr → accounts)
  const { accounts } = await import("./accounts");
  const account = accounts.active$.getValue();
  if (!account || !relay.challenge) return;

  const [mailboxEvent] = eventStore.getByFilters([
    { kinds: [10002], authors: [account.pubkey], limit: 1 },
  ]);
  const mailboxRelays = mailboxEvent
    ? mailboxEvent.tags
        .filter(
          (tag) =>
            tag[0] === "r" &&
            (!tag[2] || tag[2] === "read" || tag[2] === "write"),
        )
        .map((tag) => normalizeUrl(tag[1]))
    : [];
  const relayUrl = normalizeUrl(relay.url);
  const trusted =
    mailboxRelays.includes(relayUrl) || isActivePrivateGitRelay(relayUrl);
  if (!trusted || relay.authenticatedAs === account.pubkey) return;

  try {
    await relay.authenticate(account.signer);
  } catch (err) {
    // Non-fatal — relay may reject auth or signer may be unavailable
    console.warn(`[auth] NIP-42 auth failed for ${relay.url}:`, err);
  }
}

pool.add$.subscribe((relay) => {
  relay.challenge$
    .pipe(
      // Only act when a real challenge string arrives
      filter(Boolean),
      // Don't re-auth if the challenge string hasn't changed
      distinctUntilChanged(),
    )
    .subscribe(() => void authenticateTrustedRelay(relay));
});

// A challenge can arrive before kind 10318 has been fetched and decrypted.
// Retry challenged relays when the active account's trusted private set grows.
let previousPrivateGitRelayUrls = new Set<string>();
privateGitRelayUrls$.subscribe((urls) => {
  const trusted = new Set(urls.map(normalizeUrl));

  for (const previousUrl of previousPrivateGitRelayUrls) {
    if (trusted.has(previousUrl)) continue;
    const relay = [...pool.relays.values()].find(
      (candidate) => normalizeUrl(candidate.url) === previousUrl,
    );
    if (relay) pool.remove(relay, true);
  }
  previousPrivateGitRelayUrls = trusted;

  for (const relay of pool.relays.values()) {
    if (relay.challenge && trusted.has(normalizeUrl(relay.url))) {
      void authenticateTrustedRelay(relay);
    }
  }
});

/**
 * Tear down every connection and in-memory event associated with the previous
 * private-repository account before starting the next account's session.
 */
export function switchPrivateRepositoryAccount(pubkey?: string): void {
  const previousPrivateRelays = new Set(
    beginPrivateRepositorySession(pubkey).map(normalizeUrl),
  );

  for (const relay of [...pool.relays.values()]) {
    if (
      previousPrivateRelays.has(normalizeUrl(relay.url)) ||
      (relay.authenticatedAs !== null && relay.authenticatedAs !== pubkey)
    ) {
      pool.remove(relay, true);
    }
  }

  const privateEventIds = new Set(getPrivateRelayEventIds());
  const announcements = eventStore.getByFilters({ kinds: [REPO_KIND] });
  const privateCoordinates = new Set<string>();
  for (const announcement of announcements) {
    const identifier = announcement.tags.find(([name]) => name === "d")?.[1];
    if (!identifier) continue;
    const resolved = resolveChain(
      announcements,
      announcement.pubkey,
      identifier,
    );
    if (!resolved?.isPrivate) continue;
    for (const coordinate of resolved.allCoordinates) {
      privateCoordinates.add(coordinate);
    }
    for (const privateAnnouncement of resolved.announcements) {
      privateEventIds.add(privateAnnouncement.id);
      markPrivateRelayEvent(privateAnnouncement);
    }
  }

  if (privateCoordinates.size > 0) {
    const coordinates = [...privateCoordinates];
    const related = eventStore.getByFilters([
      { "#a": coordinates } as Filter,
      { "#A": coordinates } as Filter,
    ]);
    for (const event of related) {
      privateEventIds.add(event.id);
      markPrivateRelayEvent(event);
    }
  }

  for (const eventId of privateEventIds) {
    eventStore.remove(eventId);
  }
}

/**
 * Discover repository announcements on the active account's decrypted private
 * Git relay list. The reactive relay set tears down removed URLs and adds newly
 * trusted URLs without querying personal NIP-65 or index relays.
 */
export function startPrivateRepositoryDiscovery(): () => void {
  const sub = resilientSubscription(
    pool,
    privateGitRelayUrls$,
    [{ kinds: [REPO_KIND] }],
    {
      reconnect: true,
      gapFill: true,
      settle: false,
      paginate: false,
    },
  )
    .pipe(onlyEvents(), mapEventsToStore(eventStore))
    .subscribe({
      error: (err) =>
        console.warn("[private-repos] Discovery subscription failed:", err),
    });

  return () => sub.unsubscribe();
}

/** Max relays to use per group when resolving */
const MAX_RESOLVED_RELAYS = 5;

/**
 * Resolve a pubkey's NIP-65 mailboxes from the EventStore, fetching via
 * addressLoader if not already cached. Returns undefined if not found within
 * the timeout.
 */
async function resolveMailboxes(pubkey: string) {
  // Kick off a fetch in case the kind:10002 isn't in the store yet.
  // addressLoader writes into the EventStore as a side-effect.
  const fetchSub = addressLoader({ kind: 10002, pubkey }).subscribe();
  try {
    return await firstValueFrom(
      eventStore.model(MailboxesModel, pubkey).pipe(
        // Skip the immediate undefined emitted when the event isn't in the
        // store yet — wait for the addressLoader fetch to deliver it.
        filter(
          (m): m is { inboxes: string[]; outboxes: string[] } =>
            m !== undefined,
        ),
        timeout({ first: 3000, with: () => of(undefined) }),
      ),
    );
  } finally {
    fetchSub.unsubscribe();
  }
}

/**
 * Relay group resolver for the outbox store.
 *
 * Resolves a group ID to the current set of relay URLs:
 *
 *   Dynamic (pubkey-based):
 *   - "outbox:<pubkey>"      → that pubkey's NIP-65 write (outbox) relays
 *   - "inbox:<pubkey>"       → that pubkey's NIP-65 read (inbox) relays
 *   - "30617:<pubkey>:<d>"   → repo's declared relays from the EventStore
 *
 *   Static (settings-based):
 *   - "fallback-relays"      → user-configured fallback relays (fallbackRelays setting)
 *   - "index-relays"         → lookup/user-index relays (lookupRelays setting)
 *   - "git-index"            → git index relay (wss://index.ngit.dev)
 *   - "bootstrap-relays"     → hardcoded new-account bootstrap relays
 *
 * When the kind:10002 is not yet in the EventStore, addressLoader is used to
 * fetch it. The outbox store calls this again via reResolveRelayGroups()
 * whenever a new kind:10002 arrives, so events are sent to newly-discovered
 * relays automatically.
 */
const relayGroupResolver: RelayGroupResolver = async (groupId) => {
  // "outbox:<pubkey>" → NIP-65 write relays
  if (groupId.startsWith("outbox:")) {
    const pubkey = groupId.slice(7);
    if (!/^[0-9a-f]{64}$/.test(pubkey)) return [];
    try {
      const mailboxes = await resolveMailboxes(pubkey);
      return mailboxes?.outboxes.slice(0, MAX_RESOLVED_RELAYS) ?? [];
    } catch {
      return [];
    }
  }

  // "inbox:<pubkey>" → NIP-65 read relays
  if (groupId.startsWith("inbox:")) {
    const pubkey = groupId.slice(6);
    if (!/^[0-9a-f]{64}$/.test(pubkey)) return [];
    try {
      const mailboxes = await resolveMailboxes(pubkey);
      return mailboxes?.inboxes.slice(0, MAX_RESOLVED_RELAYS) ?? [];
    } catch {
      return [];
    }
  }

  // Repo coord: "30617:<pubkey>:<d>"
  //
  // Resolve the confirmed recursive maintainer chain so announcements from an
  // unconfirmed pubkey cannot inject a publication relay.
  const repoCoordinate = parseRepoCoordinate(groupId);
  if (repoCoordinate) {
    const { pubkey, identifier: d } = repoCoordinate;
    const announcements = eventStore.getByFilters({
      kinds: [REPO_KIND],
      "#d": [d],
    } as Filter);
    return resolveChain(announcements, pubkey, d)?.relays ?? [];
  }

  // Static settings-based groups
  if (groupId === "fallback-relays") return fallbackRelays.getValue();
  if (groupId === "index-relays") return lookupRelays.getValue();
  if (groupId === "git-index") return gitIndexRelays.getValue();
  if (groupId === "bootstrap-relays") {
    const { ACCOUNT_BOOTSTRAP_RELAYS } = await import("@/actions/account");
    return ACCOUNT_BOOTSTRAP_RELAYS;
  }

  return [];
};

outboxStore.relayGroupResolver = relayGroupResolver;

/**
 * Resolve privacy from the recursive, confirmed-maintainer announcement set.
 * A replacement announcement supplied directly to the outbox participates in
 * the prospective state even when it has not reached EventStore yet.
 */
function privateRepoCoordinatesForPublication(
  event: NostrEvent,
  groupIds: string[],
): Set<string> | undefined {
  const candidateCoordinates = new Set(groupIds);
  for (const [name, value] of event.tags) {
    if ((name === "a" || name === "A") && value) {
      candidateCoordinates.add(value);
    }
  }
  if (event.kind === REPO_KIND) {
    const identifier = event.tags.find(([name]) => name === "d")?.[1];
    if (identifier) {
      candidateCoordinates.add(`${REPO_KIND}:${event.pubkey}:${identifier}`);
    }
  }

  const privateCoordinates = new Set<string>();
  const eventIdentifier =
    event.kind === REPO_KIND
      ? event.tags.find(([name]) => name === "d")?.[1]
      : undefined;
  const outgoingIsExactlyPrivate =
    event.kind === REPO_KIND &&
    event.tags.some(
      ([name, value, ...extra]) =>
        name === "private" && value === "true" && extra.length === 0,
    );
  if (outgoingIsExactlyPrivate && eventIdentifier) {
    privateCoordinates.add(`${REPO_KIND}:${event.pubkey}:${eventIdentifier}`);
  }

  for (const candidate of candidateCoordinates) {
    const coordinate = parseRepoCoordinate(candidate);
    if (!coordinate) continue;

    let announcements = eventStore.getByFilters({
      kinds: [REPO_KIND],
      "#d": [coordinate.identifier],
    } as Filter);

    const existingForAuthor = announcements.find(
      (announcement) => announcement.pubkey === event.pubkey,
    );
    if (
      eventIdentifier === coordinate.identifier &&
      (!existingForAuthor || event.created_at > existingForAuthor.created_at)
    ) {
      announcements = [
        ...announcements.filter(
          (announcement) => announcement.pubkey !== event.pubkey,
        ),
        event,
      ];
    }

    const resolved = resolveChain(
      announcements,
      coordinate.pubkey,
      coordinate.identifier,
    );
    if (resolved?.requestedMaintainers.length && !outgoingIsExactlyPrivate) {
      throw new Error(
        "Repository maintainer privacy is still resolving; publication is disabled",
      );
    }
    if (resolved?.isPrivate) {
      for (const resolvedCoordinate of resolved.allCoordinates) {
        privateCoordinates.add(resolvedCoordinate);
      }
    }
  }

  return privateCoordinates.size > 0 ? privateCoordinates : undefined;
}

/**
 * Private repository events may only use repository-coordinate relay groups.
 * This strips author outboxes, notification inboxes, fallback/index relays,
 * and any future broad group before the outbox persists or resolves it.
 */
const privateRepoRelayGroupFilter: RelayGroupFilter = (event, groupIds) => {
  const privateCoordinates = privateRepoCoordinatesForPublication(
    event,
    groupIds,
  );
  if (!privateCoordinates) return groupIds;

  markPrivateRelayEvent(event);
  return groupIds.filter((groupId) => privateCoordinates.has(groupId));
};

void outboxStore
  .setRelayGroupFilter(privateRepoRelayGroupFilter)
  .catch((err) =>
    console.warn("[outbox] Failed to apply publication boundary:", err),
  );

/**
 * Watch for changes to the current user's NIP-65 relay list and re-resolve
 * relay groups for any pending outbox items. This ensures that if the user
 * updates their relay list, pending events are sent to any newly-added relays.
 *
 * We track the serialized outbox URL list so we only trigger on actual changes,
 * not on every MailboxesModel emission.
 */
function watchUserMailboxesForOutboxReResolve(pubkey: string): () => void {
  const sub = eventStore
    .model(MailboxesModel, pubkey)
    .pipe(
      map((m) => JSON.stringify([...(m?.outboxes ?? [])].sort())),
      distinctUntilChanged(),
    )
    .subscribe(() => {
      // Pass the pubkey so only items with outbox:<pubkey> / inbox:<pubkey>
      // / 30617:<pubkey>:* groups are re-resolved.
      outboxStore.reResolveRelayGroups(pubkey).catch((err) => {
        console.warn("[outbox] reResolveRelayGroups failed:", err);
      });
    });
  return () => sub.unsubscribe();
}

// Exported so accounts.ts (or App.tsx) can call it when the active account changes.
export { watchUserMailboxesForOutboxReResolve };

/**
 * Watch for any kind:10002 (NIP-65 relay list) arriving in the EventStore and
 * trigger outbox re-resolution for the event's author.
 *
 * This covers the notification inbox case: when we publish a comment and add
 * "inbox:<authorPubkey>" as a relay group with no URLs yet, the outbox store
 * will re-resolve it as soon as the author's kind:10002 arrives — whether
 * that's from the addressLoader fetch triggered by the resolver, or from any
 * other subscription that happens to load it.
 */
function watchAnyMailboxForOutboxReResolve(): () => void {
  const sub = eventStore.filters({ kinds: [10002] }, true).subscribe({
    next: (event) => {
      outboxStore.reResolveRelayGroups(event.pubkey).catch((err) => {
        console.warn("[outbox] reResolveRelayGroups failed:", err);
      });
    },
  });
  return () => sub.unsubscribe();
}

// Start watching immediately — this covers notification inbox re-resolution
// for any pubkey whose kind:10002 arrives after a comment is published.
watchAnyMailboxForOutboxReResolve();

/**
 * Event kinds accepted by the git index relay (wss://index.ngit.dev).
 * Any event published to "git-index" must be one of these kinds.
 */
const GIT_INDEX_KINDS = new Set([
  30617, // NIP-34 repository announcements
  10317, // User GRASP server lists
]);

/**
 * Publish an event to the configured relays.
 *
 * This is the low-level publish used by the ActionRunner for built-in
 * applesauce actions (UpdateProfile, AddOutboxRelay, etc.). It publishes to
 * the union of the provided relays and the global fallbackRelays, and
 * records the attempt in the outbox store for retry and UI display.
 *
 * For NIP-34 events (issues, status changes, renames) use the dedicated
 * Action functions in src/actions/nip34.ts which resolve the correct relay
 * groups (user outbox + repo relays + notification inboxes) automatically.
 *
 * Kind:30617 (repo announcements) and kind:10317 (GRASP lists) are
 * automatically also published to "git-index" — the git index relay only
 * accepts these two kinds, so only they should ever be sent there.
 *
 * @param event          - The signed Nostr event to publish
 * @param extraGroupIds  - Additional group IDs to publish to alongside the
 *                         user's outbox (e.g. "index-relays").
 */
export async function publish(
  event: NostrEvent,
  extraGroupIds?: string[],
): Promise<void> {
  // Add to local store immediately for optimistic updates
  eventStore.add(event);

  const groupIds = [`outbox:${event.pubkey}`, "fallback-relays"];
  if (extraGroupIds) groupIds.push(...extraGroupIds);

  // Automatically include "git-index" for kinds it accepts, deduplicating
  // in case the caller already added it explicitly.
  if (GIT_INDEX_KINDS.has(event.kind) && !groupIds.includes("git-index")) {
    groupIds.push("git-index");
  }

  await outboxStore.publish(event, groupIds);
}

/**
 * Create unified event loader for the EventStore.
 * This automatically loads events that are referenced but not in the store yet.
 *
 * Features:
 * - Automatic batching of event requests
 * - Follows relay hints from events
 * - Checks IndexedDB cache first
 * - Queries lookup relays for missing events
 */
export const eventLoader = createEventLoaderForStore(eventStore, pool, {
  cacheRequest,
  lookupRelays: lookupRelays.getValue(),
  extraRelays: fallbackRelays,
  followRelayHints: true,
  bufferTime: 1000, // Batch requests within 1 second
});

/**
 * Loader for addressable events (NIP-33).
 * Used for loading articles, profiles, and other replaceable events.
 */
export const addressLoader = createAddressLoader(pool, {
  cacheRequest,
  extraRelays: fallbackRelays,
  eventStore,
  lookupRelays: lookupRelays.getValue(),
});

/**
 * Fetch kind:10318 from every normal identity relay and wait for all requests
 * to settle before a replacement is derived.
 */
export async function refreshPrivateGitRelayList(
  pubkey: string,
): Promise<void> {
  const mailbox = eventStore.getReplaceable(10002, pubkey);
  const mailboxRelays =
    mailbox?.tags
      .filter(
        ([name, , marker]) => name === "r" && (!marker || marker === "write"),
      )
      .map(([, url]) => url) ?? [];
  const relays = [
    ...new Set([
      ...mailboxRelays,
      ...lookupRelays.getValue(),
      ...fallbackRelays.getValue(),
    ]),
  ];
  if (relays.length === 0) {
    throw new Error(
      "Cannot safely update the private Git relay list without identity relays",
    );
  }

  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      subscription.unsubscribe();
      reject(
        new Error(
          "Timed out refreshing the private Git relay list; no changes were published",
        ),
      );
    }, 10_000);
    const subscription = pool
      .request(relays, {
        kinds: [10318],
        authors: [pubkey],
      })
      .subscribe({
        next: (event) => eventStore.add(event),
        error: (err) => {
          clearTimeout(timeoutId);
          reject(err);
        },
        complete: () => {
          clearTimeout(timeoutId);
          resolve();
        },
      });
  });
}

/**
 * Settle repository discovery on every currently encrypted private relay
 * before deciding that any endpoint is unused.
 */
export async function refreshPrivateRepositoryAnnouncements(
  relayUrls: string[],
): Promise<void> {
  if (relayUrls.length === 0) return;

  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      subscription.unsubscribe();
      reject(
        new Error(
          "Timed out refreshing private repositories; endpoint removal was cancelled",
        ),
      );
    }, 10_000);
    const subscription = pool
      .request(relayUrls, { kinds: [REPO_KIND] })
      .subscribe({
        next: (event) => eventStore.add(event),
        error: (err) => {
          clearTimeout(timeoutId);
          reject(err);
        },
        complete: () => {
          clearTimeout(timeoutId);
          resolve();
        },
      });
  });
}

/**
 * Loader for reactions (kind 7).
 * Efficiently loads and caches reactions for events.
 */
export const reactionsLoader = createReactionsLoader(pool, {
  cacheRequest,
  eventStore,
});

/** Create loader for loading zaps for other events */
export const zapsLoader = createZapsLoader(pool, {
  cacheRequest,
  extraRelays: fallbackRelays,
  eventStore,
});

/**
 * Singleton profile loader for kind:0 metadata events.
 *
 * Batches all per-pubkey profile fetch requests across the entire app into a
 * single relay REQ per 200ms window. Components call this instead of opening
 * their own pool.subscription so that e.g. a list of 20 issue authors produces
 * one REQ rather than 20.
 *
 * The eventStore option deduplicates events that come back from relays.
 * Call-site deduplication (skip if already in store) is the responsibility of
 * the caller — see useLoadProfile / useProfilesForPubkeys.
 */
export const profileLoader = createAddressLoader(pool, {
  cacheRequest,
  eventStore,
  lookupRelays: lookupRelays.getValue(),
  bufferTime: 200,
});

/**
 * Loader for NIP-05 DNS identity lookups.
 * Results are persisted to IndexedDB (gitworkshop / nip05-identities) so that
 * verified identities survive page reloads. Expiry is set to 30 days so
 * stale entries are re-verified after a month.
 */
export const dnsIdentityLoader = new DnsIdentityLoader(nip05IdbCache);
dnsIdentityLoader.expiration = 60 * 60 * 24 * 30; // 30 days in seconds

// Warm the in-memory identity map from IDB on startup.
// DnsIdentityLoader.loadIdentity() reads IDB but does NOT write back to the
// in-memory map (this.identities), so getIdentity() would always miss on a
// fresh page load even when IDB has data. Loading all entries upfront ensures
// the synchronous getIdentity() check in useRepoPath / useDnsIdentity hits on
// the first render without a loading flash.
//
// nip05WarmupReady resolves once the IDB warm-up is complete. useDnsIdentity
// awaits this before deciding whether to show a loading state, so navigating
// to a NIP-05 repo URL from the landing page never flashes a loading screen
// when the identity is already cached in IDB.
export const nip05WarmupReady: Promise<void> = loadAllNip05FromIdb().then(
  (entries) => {
    for (const [address, identity] of Object.entries(entries)) {
      dnsIdentityLoader.identities.set(address, identity);
    }
  },
);

// ---------------------------------------------------------------------------
// NIP-34 singleton loaders for Issues, Patches, and PRs
//
// Each loader is a SINGLE instance per tag name, so all per-item calls
// within the buffer window are collapsed into one relay subscription.
//
// List level — essentials + comments
//   Essentials (#e tag, bufferTime: 100ms): status (1630-1633), labels (1985),
//   and deletions (5) for every item on the page.
//   Comments (#E tag, bufferTime: 500ms): NIP-22 comments (1111), PR
//   updates (1619), and CI activity (9841/9842/39842). The longer buffer ensures
//   essentials land first.
//
// Thread level — all child events, no kind restriction (detail pages only)
//   Three loaders for the three tag names used to reference thread members:
//   #e (lowercase), #E (uppercase/NIP-22 root), #q (quote).
//   No kind restriction — fetches reactions, zaps, deletions, and any other
//   events that tag a thread member. Will overlap with essentials/comments
//   data already fetched, but the EventStore deduplicates on receipt.
// ---------------------------------------------------------------------------

const NIP34_ESSENTIALS_BUFFER = 100;
const NIP34_COMMENTS_BUFFER = 500;
const NIP34_THREAD_BUFFER = 500;
/** Longer buffer for deletion-of-essentials loader — less time-critical. */
const NIP34_ESSENTIAL_DELETIONS_BUFFER = 2000;
/** Buffer for the CI results-by-commit loader — a page of commits batches
 *  into one REQ per relay within this window. */
const CI_COMMIT_RESULTS_BUFFER = 500;

/**
 * Essentials loader (#e tag).
 * Fetches status (1630-1633), NIP-32 labels (1985), deletion requests (5),
 * cover notes (1624), and legacy NIP-34 replies (kind 1 and 1622) for
 * issues/patches/PRs.
 *
 * Cover notes use lowercase #e (NIP-10 style) to reference the root item,
 * not NIP-22 uppercase #E, so they belong here alongside other essentials.
 *
 * Uses createPaginatedTagValueLoader which combines the historical fetch,
 * per-relay backward pagination, and a persistent live subscription in one.
 *
 * Legacy replies use NIP-10 #e tagging (not NIP-22 #E), so they must be
 * fetched via this #e loader rather than the #E comments loader. Including
 * them here is a bit of a hack — semantically they're comments, not
 * essentials, and they arrive earlier (100ms buffer vs 500ms for comments).
 * But legacy replies are an edge case in practice, so this is a reasonable
 * tradeoff vs. creating an additional singleton loader and subscription.
 */
export const nip34EssentialsLoader = createPaginatedTagValueLoader(pool, "e", {
  cacheRequest,
  eventStore,
  kinds: [
    1630,
    1631,
    1632,
    1633,
    1985,
    5,
    COVER_NOTE_KIND,
    ...LEGACY_REPLY_KINDS,
  ],
  bufferTime: NIP34_ESSENTIALS_BUFFER,
});

/**
 * Comments loader (#E tag).
 * Fetches NIP-22 comments (kind 1111), PR updates (kind 1619), and ngit-ci
 * activity (kinds 9841/9842/39842 — CI events tag the PR root via
 * NIP-22-style #E, so they ride along with comments and power CI badges).
 * Uses the uppercase `E` root tag, so it needs its own loader instance
 * separate from the `#e` loaders. The longer buffer ensures essentials
 * land first.
 */
export const nip34CommentsLoader = createPaginatedTagValueLoader(pool, "E", {
  cacheRequest,
  eventStore,
  kinds: [1111, 1619, ...CI_EVENT_KINDS],
  bufferTime: NIP34_COMMENTS_BUFFER,
});

/**
 * Deletion-of-essentials loader.
 *
 * Fetches kind:5 deletion requests that reference a specific essential event
 * (label event, status event, etc.) via its `#e` tag. Called once per
 * essential event ID discovered by nip34EssentialsLoader.
 *
 * Uses a longer buffer time than the essentials loader because deletion events
 * are lower-priority — they only affect display after the referenced event is
 * already known.
 *
 * Multiple calls within the buffer window are batched into a single relay REQ
 * automatically (same createPaginatedTagValueLoader mechanism).
 */
export const nip34EssentialDeletionsLoader = createPaginatedTagValueLoader(
  pool,
  "e",
  {
    cacheRequest,
    eventStore,
    kinds: [5],
    bufferTime: NIP34_ESSENTIAL_DELETIONS_BUFFER,
  },
);

/**
 * CI results-by-commit loader (#c tag).
 *
 * Fetches ngit-ci activity for specific commits — powers
 * the commit status ticks in the CodeBar commit summary row, the commit
 * history list, and the commit detail page. Callers fire it once per commit
 * they are about to display; calls within the buffer window are batched into
 * a single REQ per relay, so fetching CI for a page of commits costs one
 * subscription.
 *
 * Kind:39842 progress markers also arrive repo-wide via
 * the #a coordinate filter in nip34RepoLoader's repo meta subscription (their
 * NIP-40 expiration keeps that set small) and are read back from the store
 * by #c when rolling up a commit's status.
 */
export const ciResultsByCommitLoader = createPaginatedTagValueLoader(
  pool,
  "c",
  {
    cacheRequest,
    eventStore,
    kinds: [...CI_EVENT_KINDS],
    bufferTime: CI_COMMIT_RESULTS_BUFFER,
  },
);

/**
 * Thread loader — replies (#e tag). All events referencing a thread member
 * via lowercase `e` tag. No kind restriction. Only fired on detail pages.
 */
const nip34ThreadReplyLoader = createPaginatedTagValueLoader(pool, "e", {
  cacheRequest,
  eventStore,
  bufferTime: NIP34_THREAD_BUFFER,
});

/**
 * Thread loader — root references (#E tag). All events referencing a thread
 * member via uppercase `E` tag (NIP-22 root reference). No kind restriction.
 * Only fired on detail pages.
 */
const nip34ThreadRootLoader = createPaginatedTagValueLoader(pool, "E", {
  cacheRequest,
  eventStore,
  bufferTime: NIP34_THREAD_BUFFER,
});

/**
 * Thread loader — quotes (#q tag). All events quoting a thread member.
 * No kind restriction. Only fired on detail pages.
 */
const nip34ThreadQuoteLoader = createPaginatedTagValueLoader(pool, "q", {
  cacheRequest,
  eventStore,
  bufferTime: NIP34_THREAD_BUFFER,
});

// ---------------------------------------------------------------------------
// NIP-34 observable factories
//
// Pure RxJS observable factories — no React, no hooks. Consumed by React
// hooks via use$(), which handles subscription lifecycle.
//
// Two levels, each non-additive (no overlap):
//
//   nip34ListLoader    — list level: essentials + comments for a single item.
//                        Also used by nip34RepoLoader to fire both loaders
//                        for each newly discovered item.
//
//   nip34ThreadItemLoader — thread level: ALL events referencing the root
//                        or any comment via #e, #E, or #q tags (no kind
//                        restriction). Recursively fetches child events
//                        for each comment. Only fired on detail pages.
//
//   nip34RepoLoader    — repo level: subscribes to all items for a set of
//                        repo coordinates and pipes each newly discovered
//                        item ID into nip34ListLoader.
//
// Each loader is a createPaginatedTagValueLoader instance that handles the
// historical fetch, per-relay backward pagination, and persistent live
// subscription in one. Calls within the same buffer window are batched into
// a single relay subscription per relay automatically.
// ---------------------------------------------------------------------------

/** All root item kinds tracked at the repo level (issues + PR root kinds). */
const REPO_ITEM_KINDS = [ISSUE_KIND, ...PR_ROOT_KINDS] as const;

/** Kind 7 reaction — used for repo stars. */
const REACTION_KIND = 7;

/** Kind 10018 — NIP-51 Git repositories follow list; used for repo follower counts. */
const GIT_REPOS_FOLLOW_KIND = 10018 as const;

/**
 * List-level loader for a single item.
 *
 * Fires both essentials (status, labels, deletions) and comments loaders for
 * the given item ID against the provided relay list. Each loader handles its
 * own historical fetch (backward pagination until exhausted) and persistent
 * live subscription.
 *
 * The relay list is a static snapshot — callers are responsible for
 * reactivity. nip34RepoLoader re-fires this function when the RelayGroup
 * gains new relays. useNip34ItemLoader re-subscribes via use$() when
 * repoRelayKey changes (driven by useRelayGroupUrls).
 *
 * Because nip34EssentialsLoader and nip34CommentsLoader are singleton
 * instances, calls within the same buffer window are batched into a single
 * REQ per relay automatically.
 *
 * @param itemId - The event ID of the issue / patch / PR
 * @param relays - Relay URLs to query (snapshot at call time)
 */
export function nip34ListLoader(
  itemId: string,
  relays: string[],
): Observable<PaginatedTagValueResponse> {
  return new Observable<PaginatedTagValueResponse>((subscriber) => {
    // Track essential event IDs we have already fired the deletion loader for
    // so we don't duplicate subscriptions when the loader delivers the same
    // event more than once.
    const seenEssentialIds = new Set<string>();

    const essentialsSub = nip34EssentialsLoader({
      value: itemId,
      relays,
    }).subscribe({
      next: (msg) => {
        subscriber.next(msg);
        if (msg !== "EOSE") {
          const event = msg as NostrEvent;
          if (!seenEssentialIds.has(event.id)) {
            seenEssentialIds.add(event.id);
            // Fire deletion loader for this essential event's ID.
            // Calls within the buffer window are batched automatically.
            nip34EssentialDeletionsLoader({
              value: event.id,
              relays,
            }).subscribe(subscriber);
          }
        }
      },
    });

    const commentsSub = nip34CommentsLoader({
      value: itemId,
      relays,
    }).subscribe({
      next: (msg) => subscriber.next(msg),
    });

    return () => {
      essentialsSub.unsubscribe();
      commentsSub.unsubscribe();
    };
  });
}

/**
 * Max inbox relay URLs to query per item author when resolveAuthorInbox is
 * enabled. Keeps the relay connection count bounded even for authors with many
 * read relays declared in their kind:10002.
 */
const MAX_AUTHOR_INBOX_RELAYS = 3;

/**
 * Supplemental relay loader for outbox/uncensored mode.
 *
 * Mirrors nip34RepoLoader but targets the extra maintainer mailbox relay group
 * (extraRelaysForMaintainerMailboxCoverage). For each newly discovered root
 * item it calls nip34ListLoader against those extra relay URLs so that status
 * events (1630-1633), labels (1985), and comments (1111) published only to
 * maintainer or author outbox relays are also fetched — not just the root
 * events.
 *
 * Does NOT subscribe to meta events (reactions, follow lists) — those are
 * covered by nip34RepoLoader against the base relay group.
 *
 * @param coords     - Sorted array of repo coordinate strings
 * @param relayGroup - The supplemental RelayGroup (extra maintainer mailboxes)
 */
export function nip34SupplementalRelayLoader(
  coords: string[],
  relayGroup: RelayGroup,
): Observable<NostrEvent> {
  const resolveAuthorInbox = relayCurationMode.getValue() === "outbox";

  return new Observable<NostrEvent>((subscriber) => {
    const seenIds = new Set<string>();
    const knownRelayUrls = new Set<string>();
    const inboxSubs = new Subscription();

    function fireLoaders(id: string, relays: string[]): void {
      nip34ListLoader(id, relays).subscribe({
        next: (msg) => {
          if (msg !== "EOSE") subscriber.next(msg as NostrEvent);
        },
      });
    }

    function fireAuthorInboxLoaders(ev: NostrEvent): void {
      const perItemQueried = new Set(knownRelayUrls);
      addressLoader({ kind: 10002, pubkey: ev.pubkey }).subscribe();
      const s = eventStore
        .model(MailboxesModel, ev.pubkey)
        .pipe(
          filter((m): m is NonNullable<typeof m> => m !== undefined),
          take(1),
        )
        .subscribe((mailboxes) => {
          const newRelays = mailboxes.inboxes
            .slice(0, MAX_AUTHOR_INBOX_RELAYS)
            .map(normalizeUrl)
            .filter((r) => !perItemQueried.has(r));
          if (newRelays.length === 0) return;
          for (const r of newRelays) perItemQueried.add(r);
          nip34ListLoader(ev.id, newRelays).subscribe({
            next: (msg) => {
              if (msg !== "EOSE") subscriber.next(msg as NostrEvent);
            },
          });
        });
      inboxSubs.add(s);
    }

    // When new relays join the group, re-fire existing items' loaders against
    // those new relays only.
    const relaySub = relayGroupUrls$(relayGroup)
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a.length === b.length && a.every((url) => knownRelayUrls.has(url)),
        ),
      )
      .subscribe((currentUrls) => {
        const newUrls = currentUrls.filter((url) => !knownRelayUrls.has(url));
        for (const url of newUrls) knownRelayUrls.add(url);
        if (newUrls.length === 0) return;
        for (const id of seenIds) {
          fireLoaders(id, newUrls);
        }
      });

    const filters = [{ kinds: [...REPO_ITEM_KINDS], "#a": coords } as Filter];
    const itemSub = resilientSubscription(
      pool,
      relayGroupUrls$(relayGroup),
      filters,
      {
        reconnect: true,
        gapFill: true,
        settle: false,
      },
    )
      .pipe(onlyEvents(), mapEventsToStore(eventStore))
      .subscribe({
        next: (event) => {
          const ev = event as NostrEvent;
          if (!seenIds.has(ev.id)) {
            seenIds.add(ev.id);
            fireLoaders(ev.id, [...knownRelayUrls]);
            if (resolveAuthorInbox) fireAuthorInboxLoaders(ev);
          }
        },
        complete: () => subscriber.complete(),
      });

    return () => {
      relaySub.unsubscribe();
      itemSub.unsubscribe();
      inboxSubs.unsubscribe();
    };
  });
}

/**
 * Repo-level observable factory.
 *
 * Subscribes to all NIP-34 root items (issues + PR/patch roots) for the given
 * repository coordinates via the relay group. For each newly discovered item
 * ID, calls nip34ListLoader so essentials and comments are fetched.
 *
 * Deduplication: a seenIds Set in the closure ensures each item ID is
 * submitted to the loaders exactly once, regardless of how many times the
 * relay re-delivers the root event. The set is fresh per subscription —
 * navigating away and back creates a new observable with a new set,
 * triggering a fresh fetch.
 *
 * Because nip34EssentialsLoader and nip34CommentsLoader are singleton
 * instances backed by batchLoader, all per-item calls within each loader's
 * bufferTime window are collapsed into a single relay subscription — so N
 * items produce one essentials REQ and one comments REQ, not 2N REQs.
 *
 * @param coords     - Sorted array of repo coordinate strings
 * @param relayGroup - Relay group from useResolvedRepository
 */
export function nip34RepoLoader(
  coords: string[],
  relayGroup: RelayGroup,
): Observable<NostrEvent> {
  const resolveAuthorInbox = relayCurationMode.getValue() === "outbox";

  return new Observable<NostrEvent>((subscriber) => {
    const seenIds = new Set<string>();
    const knownRelayUrls = new Set<string>();
    const inboxSubs = new Subscription();

    function fireLoaders(id: string, relays: string[]): void {
      nip34ListLoader(id, relays).subscribe({
        next: (msg) => {
          if (msg !== "EOSE") subscriber.next(msg as NostrEvent);
        },
      });
    }

    function fireAuthorInboxLoaders(ev: NostrEvent): void {
      const perItemQueried = new Set(knownRelayUrls);
      addressLoader({ kind: 10002, pubkey: ev.pubkey }).subscribe();
      const s = eventStore
        .model(MailboxesModel, ev.pubkey)
        .pipe(
          filter((m): m is NonNullable<typeof m> => m !== undefined),
          take(1),
        )
        .subscribe((mailboxes) => {
          const newRelays = mailboxes.inboxes
            .slice(0, MAX_AUTHOR_INBOX_RELAYS)
            .map(normalizeUrl)
            .filter((r) => !perItemQueried.has(r));
          if (newRelays.length === 0) return;
          for (const r of newRelays) perItemQueried.add(r);
          nip34ListLoader(ev.id, newRelays).subscribe({
            next: (msg) => {
              if (msg !== "EOSE") subscriber.next(msg as NostrEvent);
            },
          });
        });
      inboxSubs.add(s);
    }

    // When new relays join the group, re-fire existing items' loaders against
    // those new relays only. createPaginatedTagValueLoader batches all these
    // calls within its buffer window into a single REQ per relay.
    const relaySub = relayGroupUrls$(relayGroup)
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a.length === b.length && a.every((url) => knownRelayUrls.has(url)),
        ),
      )
      .subscribe((currentUrls) => {
        const newUrls = currentUrls.filter((url) => !knownRelayUrls.has(url));
        for (const url of newUrls) knownRelayUrls.add(url);
        if (newUrls.length === 0) return;
        for (const id of seenIds) {
          fireLoaders(id, newUrls);
        }
      });

    const itemFilters = [
      { kinds: [...REPO_ITEM_KINDS], "#a": coords } as Filter,
    ];
    const itemSub = resilientSubscription(
      pool,
      relayGroupUrls$(relayGroup),
      itemFilters,
      { reconnect: true, gapFill: true, settle: false },
    )
      .pipe(onlyEvents(), mapEventsToStore(eventStore))
      .subscribe({
        next: (event) => {
          const ev = event as NostrEvent;
          if (!seenIds.has(ev.id)) {
            seenIds.add(ev.id);
            // knownRelayUrls is already populated by relaySub above
            // (relayGroupUrls$ is a BehaviorSubject so relaySub fires
            // synchronously before itemSub can emit).
            fireLoaders(ev.id, [...knownRelayUrls]);
            if (resolveAuthorInbox) fireAuthorInboxLoaders(ev);
          }
        },
        complete: () => subscriber.complete(),
      });

    const repoMetaFilters = [
      {
        // Reactions (stars), follow lists, zaps — plus kind:39842 CI
        // workflow progress markers. The markers carry a NIP-40
        // expiration so the live set for a repo stays small; fetching them
        // repo-wide via #a keeps pending indicators available everywhere
        // (PR lists, PR pages) without per-item subscriptions.
        kinds: [REACTION_KIND, GIT_REPOS_FOLLOW_KIND, 9735, CI_RUN_KIND],
        "#a": coords,
      } as Filter,
    ];
    const repoMetaSub = resilientSubscription(
      pool,
      relayGroupUrls$(relayGroup),
      repoMetaFilters,
      { reconnect: true, gapFill: true, settle: false },
    )
      .pipe(onlyEvents(), mapEventsToStore(eventStore))
      .subscribe();

    return () => {
      relaySub.unsubscribe();
      itemSub.unsubscribe();
      repoMetaSub.unsubscribe();
      inboxSubs.unsubscribe();
    };
  });
}

/**
 * Fire all three thread loaders for a single event ID against a specific
 * relay list (static snapshot at call time).
 */
function nip34ThreadLoadAll(
  itemId: string,
  relays: string[],
): Observable<PaginatedTagValueResponse> {
  return merge(
    nip34ThreadReplyLoader({ value: itemId, relays }),
    nip34ThreadRootLoader({ value: itemId, relays }),
    nip34ThreadQuoteLoader({ value: itemId, relays }),
  );
}

/**
 * Thread-level loader for a single item (detail pages only).
 *
 * Fetches ALL events that reference the root item or any of its comments
 * via #e, #E, or #q tags — no kind restriction. This includes reactions,
 * zaps, deletions, quotes, and any other referencing events.
 *
 * Does NOT re-fire essentials or comments — those are handled separately
 * by nip34ListLoader (already called at the repo/list level). The no-kind
 * thread loaders will return some of the same events (the EventStore
 * deduplicates on receipt).
 *
 * For each comment discovered by nip34CommentsLoader, all three thread
 * loaders are fired recursively so child events on individual comments
 * (reactions, zaps, deletions, etc.) are also fetched.
 *
 * For each revision root patch (kind:1617 with t:root-revision) discovered
 * by nip34ThreadReplyLoader on the root item, all three thread loaders are
 * also fired so the individual commit patches in the revision (which reference
 * the revision root via #e, not the original root) are fetched.
 *
 * A seenIds set prevents duplicate loader calls within the same subscription
 * lifetime across both recursion paths. Because the thread loaders are
 * singleton batching loaders, all per-comment/per-revision calls within their
 * bufferTime window are collapsed into a single relay subscription per tag name.
 *
 * Reactive relay list: accepts Observable<string[]> | string[]. When the
 * observable emits new relay URLs, loaders are re-fired for all already-seen
 * comment IDs against only the new relays — existing subscriptions are
 * untouched. New comments are always fetched against all current relays.
 *
 * @param itemId - The event ID of the issue / patch / PR
 * @param relays - Relay URLs to query (reactive or static)
 */
export function nip34ThreadItemLoader(
  itemId: string,
  relays: Observable<string[]> | string[],
): Observable<PaginatedTagValueResponse> {
  return new Observable<PaginatedTagValueResponse>((subscriber) => {
    // seenIds: all comment IDs discovered so far (root item + comments)
    const seenIds = new Set<string>();
    // knownRelayUrls: relay URLs we have already fired loaders against
    const knownRelayUrls = new Set<string>();

    // Fire all three thread loaders for an item against a specific relay list.
    // Results are forwarded to the outer subscriber and also inspected by
    // handleRevisionRootEvent so revision root patches trigger recursion.
    function fireThreadLoaders(id: string, relayList: string[]): void {
      nip34ThreadLoadAll(id, relayList).subscribe({
        next: (msg) => {
          subscriber.next(msg);
          handleRevisionRootEvent(msg);
        },
      });
    }

    // Subscribe to relay list changes. When new relay URLs appear, re-fire
    // loaders for all already-seen IDs against only the new relays.
    const relays$ = Array.isArray(relays)
      ? (new Observable<string[]>((sub) => {
          sub.next(relays);
          sub.complete();
        }) as Observable<string[]>)
      : relays;

    // Shared handler: when a comment (kind:1111/1619) is discovered, fire
    // thread loaders for it recursively so its own children are fetched.
    function handleCommentEvent(msg: PaginatedTagValueResponse): void {
      if (msg === "EOSE") return;
      const event = msg as NostrEvent;
      if (!seenIds.has(event.id)) {
        seenIds.add(event.id);
        fireThreadLoaders(event.id, [...knownRelayUrls]);
      }
    }

    // Shared handler: when a revision root patch (kind:1617 with
    // t:root-revision / t:revision-root) is discovered via #e, fire thread
    // loaders for it so its child commit patches are fetched.
    function handleRevisionRootEvent(msg: PaginatedTagValueResponse): void {
      if (msg === "EOSE") return;
      const event = msg as NostrEvent;
      if (event.kind !== PATCH_KIND) return;
      const isRevisionRoot = event.tags.some(
        ([name, val]) =>
          name === "t" && (val === "root-revision" || val === "revision-root"),
      );
      if (!isRevisionRoot) return;
      if (!seenIds.has(event.id)) {
        seenIds.add(event.id);
        fireThreadLoaders(event.id, [...knownRelayUrls]);
      }
    }

    // Fire recursive loaders (comments only) for a given item ID against a
    // specific relay list. nip34ThreadReplyLoader is NOT called here — it is
    // already called inside fireThreadLoaders (via nip34ThreadLoadAll) and its
    // results are piped through handleRevisionRootEvent there. Calling it again
    // here would duplicate relay requests.
    function fireRecursiveLoaders(id: string, relayList: string[]): void {
      nip34CommentsLoader({ value: id, relays: relayList }).subscribe({
        next: handleCommentEvent,
      });
    }

    const relaySub = relays$
      .pipe(
        map((urls) => urls.map(normalizeUrl)),
        distinctUntilChanged(
          (a, b) =>
            a.length === b.length && a.every((url) => knownRelayUrls.has(url)),
        ),
      )
      .subscribe((currentUrls) => {
        const newUrls = currentUrls.filter((url) => !knownRelayUrls.has(url));
        for (const url of newUrls) knownRelayUrls.add(url);
        if (newUrls.length === 0) return;

        // Re-fire thread loaders and recursive loaders for all already-seen
        // IDs against only the new relays.
        // createPaginatedTagValueLoader batches these into one REQ per relay.
        for (const id of seenIds) {
          fireThreadLoaders(id, newUrls);
          fireRecursiveLoaders(id, newUrls);
        }
      });

    // Fire thread loaders for the root item — uses all currently known relays
    // (relaySub fires synchronously above since relays$ emits immediately).
    seenIds.add(itemId);
    fireThreadLoaders(itemId, [...knownRelayUrls]);

    // Fire recursive loaders for the root item against the initial relay set.
    // New relays arriving later are handled in relaySub above.
    fireRecursiveLoaders(itemId, [...knownRelayUrls]);

    return () => {
      relaySub.unsubscribe();
    };
  });
}
